/**
 * ThemeStyles.ts
 * 
 * This file contains the theme configuration for the McKenzie Portal application.
 * It defines colors, typography, spacing, shadows, and other design tokens used throughout the app.
 */

export const ThemeColors = {
  // Brand Colors
  primary: {
    main: '#1a428a', // Corporate blue from Nexis logo
    light: '#4a6ab8',
    dark: '#112d5c',
    contrastText: '#FFFFFF',
  },
  secondary: {
    main: '#6bb05e', // Corporate green from Nexis logo
    light: '#94c98d',
    dark: '#4a7a41',
    contrastText: '#FFFFFF',
  },
  accent: {
    main: '#2FB8C9', // Teal accent bridging green & blue
    light: '#7BDDE6',
    dark: '#1F8491',
    contrastText: '#0E2A33',
  },
  
  // Semantic Colors
  success: {
    main: '#4CAF50',
    light: '#81C784',
    dark: '#388E3C',
  },
  warning: {
    main: '#FFC107',
    light: '#FFD54F',
    dark: '#FFA000',
  },
  error: {
    main: '#F44336',
    light: '#E57373',
    dark: '#D32F2F',
  },
  info: {
    main: '#2196F3',
    light: '#64B5F6',
    dark: '#1976D2',
  },
  
  // Grayscale
  gray: {
    50: '#FAFAFA',
    100: '#F5F5F5',
    200: '#EEEEEE',
    300: '#E0E0E0',
    400: '#BDBDBD',
    500: '#9E9E9E',
    600: '#757575',
    700: '#616161',
    800: '#424242',
    900: '#212121',
  },
  
  // Background Colors
  background: {
    default: '#F5F9F1', // Soft green tint
    paper: '#FFFFFF',
    cards: '#FFFFFF',
    sidebar: '#FFFFFF',
    header: '#F0F5FF',
    secondary: '#E6F0FF',
    tertiary: '#F5F5F5',
    light: '#FAFAFA',
    primary: '#FFFFFF',
  },
  
  // Text Colors - Improved contrast ratios for WCAG AA compliance
  text: {
    primary: '#10212B',
    secondary: '#35424D',
    disabled: '#9E9E9E',
    hint: '#5A6B78',
  },
  
  // Border Colors
  border: {
    light: '#E0E0E0',
    medium: '#BDBDBD',
    dark: '#9E9E9E',
    main: '#C7CED9',
  },
  
  // Miscellaneous
  divider: '#DAE2EC',
  overlay: 'rgba(16, 33, 43, 0.55)',
  shadow: 'rgba(15, 40, 62, 0.12)',
  
  // Focus and interaction states
  focus: {
    ring: 'rgba(31, 75, 153, 0.35)',
    ringWidth: '2px',
    outline: 'none',
  },
  
  // High contrast mode support
  highContrast: {
    enabled: false,
    primary: '#000000',
    secondary: '#FFFFFF',
    background: '#FFFFFF',
    text: '#000000',
  },
};

export const Spacing = {
  // Base spacing unit in pixels
  unit: 8,
  
  // Common spacing values
  xs: '4px',
  sm: '8px',
  md: '16px',
  lg: '24px',
  xl: '32px',
  xxl: '48px',
};

export const Typography = {
  fontFamily: {
    primary: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
    secondary: "'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
    monospace: "'Roboto Mono', monospace",
    mono: "'Roboto Mono', monospace",
  },
  
  fontSize: {
    xs: '0.75rem',    // 12px
    sm: '0.875rem',   // 14px
    base: '1rem',     // 16px
    md: '1.125rem',   // 18px
    lg: '1.25rem',    // 20px
    xl: '1.5rem',     // 24px
    xxl: '2rem',      // 32px
    xxxl: '2.5rem',   // 40px
  },
  
  fontWeight: {
    light: 300,
    regular: 400,
    medium: 500,
    semiBold: 600,
    semibold: 600,
    bold: 700,
  },
  
  lineHeight: {
    tight: 1.2,
    normal: 1.5,
    relaxed: 1.7,
  },
  
  letterSpacing: {
    tight: '-0.01em',
    normal: '0',
    wide: '0.01em',
  },
};

export const BorderRadius = {
  xs: '2px',
  sm: '4px',
  md: '8px',
  lg: '12px',
  xl: '16px',
  xxl: '24px',
  round: '50%',
  full: '9999px',
  small: '4px',
  medium: '8px',
  large: '12px',
};

export const Shadows = {
  none: 'none',
  xs: '0 1px 2px rgba(0, 0, 0, 0.05)',
  sm: '0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)',
  md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
  lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
  xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  card: '0 2px 8px rgba(0, 0, 0, 0.08)',
  dropdown: '0 4px 12px rgba(0, 0, 0, 0.15)',
  inner: 'inset 0 2px 4px rgba(0, 0, 0, 0.06)',
};

export const Transitions = {
  duration: {
    shortest: '150ms',
    short: '200ms',
    standard: '300ms',
    long: '400ms',
    medium: '250ms',
  },
  easing: {
    easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
    easeOut: 'cubic-bezier(0.0, 0, 0.2, 1)',
    easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
    sharp: 'cubic-bezier(0.4, 0, 0.6, 1)',
  },
  smooth: 'all 0.2s ease',
};

export const ZIndex = {
  appBar: 1100,
  drawer: 1200,
  modal: 1300,
  snackbar: 1400,
  tooltip: 1500,
};

export const Breakpoints = {
  mobile: '480px',
  tablet: '768px',
  desktop: '1024px',
  wide: '1280px',
  ultraWide: '1920px',
};

export const MediaQueries = {
  mobile: `@media (max-width: ${Breakpoints.tablet})`,
  tablet: `@media (min-width: ${Breakpoints.tablet}) and (max-width: ${Breakpoints.desktop})`,
  desktop: `@media (min-width: ${Breakpoints.desktop})`,
  wide: `@media (min-width: ${Breakpoints.wide})`,
  // Legacy breakpoints for backward compatibility
  xs: `@media (max-width: ${Breakpoints.tablet})`,
  sm: `@media (min-width: ${Breakpoints.tablet})`,
  md: `@media (min-width: ${Breakpoints.desktop})`,
  lg: `@media (min-width: ${Breakpoints.wide})`,
  xl: `@media (min-width: ${Breakpoints.ultraWide})`,
};

// Component-specific styles - Only keeping actively used styles
export const ComponentStyles = {
  sidebar: {
    width: '280px',
    collapsedWidth: '80px',
  },
  header: {
    height: '80px',
  },
};
